import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import useLocale from 'next-translate/useTranslation';
import Link from 'next/link';
import clsxm from '@/lib/clsxm';
import { videoModelsWithDescription } from '@/constants/videoModels';
import ButtonLink from '@/components/links/ButtonLink';

const Marquee = ({
  children,
  direction = 'left',
  duration = 40,
}: {
  children: React.ReactNode;
  direction?: 'left' | 'right';
  duration?: number;
}) => {
  return (
    <div className='group/marquee flex overflow-hidden py-4 select-none'>
      <div
        className='flex flex-shrink-0 gap-4 pr-4 group-hover/marquee:[animation-play-state:paused]'
        style={{
          animation: `marquee-${direction} ${duration}s linear infinite`,
        }}
      >
        {children}
        {children}
      </div>
    </div>
  );
};

const ModelCardContent = ({ model, t }: { model: any; t: any }) => (
  <>
    <div className='mb-3 flex items-center gap-3'>
      <div
        className={clsxm(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 dark:bg-slate-700/50',
        )}
      >
        {model.icon ? (
          <model.icon className='h-6 w-6 text-gray-700 dark:text-gray-200' />
        ) : (
          <span className='text-sm font-bold text-gray-400'>AI</span>
        )}
      </div>
      <div className='flex flex-col min-w-0'>
        <h3 className='font-bold text-gray-900 dark:text-white text-sm line-clamp-1'>
          {model.title}
        </h3>
        <div className='flex items-center gap-1.5 mt-0.5'>
          {model.comingSoon && (
            <span className='inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400'>
              Coming Soon
            </span>
          )}
          {model.isNew && !model.comingSoon && (
            <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-400'>
              New
            </span>
          )}
          {model.isBest && (
            <span className='inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/30 px-1.5 py-0.5 text-[10px] font-medium text-purple-700 dark:text-purple-400'>
              Best
            </span>
          )}
        </div>
      </div>
    </div>

    <p className='text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed'>
      {t(model.description)}
    </p>
  </>
);

const cardClasses = clsxm(
  'flex w-72 flex-col rounded-xl border p-4 transition-all hover:shadow-md',
  'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700/50',
  'hover:border-purple-500/30 dark:hover:border-purple-500/30',
);

const ModelCard = ({ model, t }: { model: any; t: any }) => {
  if (model.landingPage) {
    return (
      <Link href={model.landingPage} className={cardClasses}>
        <ModelCardContent model={model} t={t} />
      </Link>
    );
  }

  return (
    <div className={cardClasses}>
      <ModelCardContent model={model} t={t} />
    </div>
  );
};

// Deterministic shuffle to prevent hydration mismatches
const getSeededRandom = (seed: number) => {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

const shuffleArray = <T,>(array: T[], seed: number): T[] => {
  const arr = [...array];
  const random = getSeededRandom(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export default function VideoModelsShowcase() {
  const { t } = useLocale('ai-videos');

  // Use useMemo with a fixed seed to ensure consistent ordering on server and client
  const shuffledModels = useMemo(() => {
    return shuffleArray(videoModelsWithDescription, 42);
  }, []);

  // Split models into two roughly equal arrays
  const midPoint = Math.ceil(shuffledModels.length / 2);
  const row1Models = shuffledModels.slice(0, midPoint);
  const row2Models = shuffledModels.slice(midPoint);

  return (
    <section className='relative overflow-hidden bg-gray-50 py-24 dark:bg-slate-900/50'>
      {/* Background gradients */}
      <div className='absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-gray-50 to-transparent z-10 dark:from-slate-900/50' />
      <div className='absolute top-0 right-0 h-full w-20 bg-gradient-to-l from-gray-50 to-transparent z-10 dark:from-slate-900/50' />

      <div className='relative z-0 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-12'>
        <div className='text-center'>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'
          >
            {t('World-Class AI Video Models')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'
          >
            {t(
              "Access the industry's most powerful video generation models in one place.",
            )}
          </motion.p>
        </div>
      </div>

      <div className='space-y-4'>
        <Marquee direction='left' duration={60}>
          {row1Models.map((model) => (
            <ModelCard key={model.id} model={model} t={t} />
          ))}
        </Marquee>

        <Marquee direction='right' duration={60}>
          {row2Models.map((model) => (
            <ModelCard key={model.id} model={model} t={t} />
          ))}
        </Marquee>
      </div>

      <div className='mt-12 flex justify-center relative z-10'>
        <ButtonLink
          href='/ai-videos'
          variant='dark'
          className='px-8 py-3 text-lg'
        >
          {t('Start Creating Videos')}
        </ButtonLink>
      </div>
    </section>
  );
}
